import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import Select, { components } from 'react-select';
import ReactTooltip from 'react-tooltip';
import { LessonMode } from '../../../Constants';

import ColorPicker from '../tools/ColorPicker';
import DrawOverlayService, { getDrawOverlayWidthPx } from '../../services/DrawOverlayService';
import PlayerService from '../../services/PlayerService';

import RichTextEditor, { ADVANCED_CKEDITOR_TOOLBAR, ADVANCED_MATH_CKEDITOR_TOOLBAR, MATH_TOOLBAR, MATH_WITH_TABLE_TOOLBAR, MINIMUM_TOOLBAR } from '../RichTextEditor';

import HtmlComponent from '../HtmlComponent';

import '../../../css/Draw.scss';
import '../../../css/DrawOverlay.scss';
import useStyleEvents from '../../../hooks/useStyleEvents';
import useBoundedStickyToolbar from '../../../hooks/useBoundedStickyToolbar';
import { useTextHighlight } from '../../../hooks/useTextHighlight';
import responseService from '../../services/ResponseService';
import navMenuService from '../../services/NavMenuService';
import highlightAnnotationService from '../../services/HighlightAnnotationService';
import { register } from '../../../i18n';
import PrintQuestionNumber from './PrintQuestionNumber';
import ShowPreviousResponseButton from '../ShowPreviousResponseButton';
import SubmissionWarningDialog from '../SubmissionWarningDialog';
import ShowPreviousResponseConfirmDialog from '../ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from '../PreviewUnavailableDialog';

const t = register('GlobalQuestionLabels');
const t2 = register('AriaLabels');

const { ValueContainer, Placeholder, SingleValue } = components;

const CustomValueContainer = ({ children, ...props }) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(children, (child) => {
        return child && child.type !== Placeholder && child.type !== SingleValue ? child : null;
      })}
    </ValueContainer>
  );
};

const brushSizeOptions = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

const brushSizeCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '5px 7px',
    margin: 0,
  }),
  control: (_provided, _state) => ({
    width: 50,
    height: 20,
    padding: 0,
    margin: 0,
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '-4px',
    marginTop: '-2px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '4px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  singleValue: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  })
};

const shapeCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '4px 0',
    margin: '0 -2px',
  }),
  control: (_provided, _state) => ({
    height: 20,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const lineCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '0px 0px',
    margin: '0px 8px',
  }),
  control: (_provided, _state) => ({
    height: 20,
    width: 70,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '8px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const pointCustomStyles = {
  option: (provided, _state) => ({
    ...provided,
    padding: '4px 0',
    margin: 0,
  }),
  control: () => ({
    height: 20,
    width: 60,
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 100,
    marginLeft: '12px',
  }),
  input: () => ({
    color: 'transparent',
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    padding: 0,
    marginLeft: '4px',
    marginTop: '-2px',
  }),
};

const brushSizePlaceholder = <div className='brushSizeToolButton drawToolbarButton' />;
const shapePlaceholder = <div className='shapeToolButton drawToolbarButton' />;
const linePlaceholder = <div className='lineToolButton drawToolbarButton' />;
const pointPlaceholder = <div className='pointToolButton drawToolbarButton' />;
const DRAW_OVERLAY_TOOLBAR_HEIGHT_PX = 0;
const DRAW_OVERLAY_MIN_HEIGHT_PX = 400;

const DrawOverlay = observer(({ lessonElementId }) => {
  const {
    drawManager,
    lessonManager,
    responseManager,
    toolbarManager,
    highlightAnnotationManager,
  } = useContext(MobXProviderContext);

  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [rerender, setRerender] = useState(false);
  const textMeasureRef = useRef(null);
  const initializedRef = useRef(false);

  const model = lessonManager.getSlideModel(lessonElementId);
  const drawOverlayService = [];
  drawOverlayService[lessonElementId] = useMemo(() => new DrawOverlayService(model), [lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps
  drawManager.init(lessonElementId);

  useTextHighlight(textMeasureRef, lessonElementId);

  useEffect(() => {
    if (toolbarManager.isHighlightAnnotationOpen || toolbarManager.isToolOn('StickyNotes')) {
      highlightAnnotationService.showHighlights(lessonElementId);
    }
  });

  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const readOnly = lessonElementState ? (lessonElementState.readOnly || lessonManager.isReadOnly) : false;

  const directions = model.directions || '';
  const questionStemHtml = model.questionText || '';

  useLayoutEffect(() => {
    const el = textMeasureRef.current;
    if (!el) {
      return undefined;
    }
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setContentHeight(Math.max(Math.ceil(rect.height ), DRAW_OVERLAY_MIN_HEIGHT_PX));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [questionStemHtml]);

  useStyleEvents(lessonElementId);
  useBoundedStickyToolbar(lessonElementId);

  useEffect(() => {
    initializedRef.current = false;
  }, [lessonElementId]);

  useEffect(() => {
    if (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) {
      return;
    }
    if (contentHeight < 1) {
      return;
    }

    model.graphWidth = getDrawOverlayWidthPx(model);
    model.graphHeight = contentHeight;

    const init = async () => {
      const svc = drawOverlayService[lessonElementId];
      if (!initializedRef.current) {
        await svc.initialize();
        await svc.runtimeObjectReady();
        await svc.showResponse();
        initializedRef.current = true;
      } else {
        svc.resizeToHeight(contentHeight);
      }
      setRerender(!rerender);
    };
    init();
  }, [contentHeight, lessonElementId, model.questionWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lessonElementState && lessonElementState.currentResponseAnswer && drawOverlayService[lessonElementId]) {
      const timer = setTimeout(() => {
        drawOverlayService[lessonElementId].showResponse();
        setRerender(!rerender);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [lessonElementState?.currentResponseAnswer, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (drawOverlayService[lessonElementId] && readOnly) {
      drawOverlayService[lessonElementId].setDrawingMode(false);
      drawOverlayService[lessonElementId].deselectToolbarButtons();
    }
  }, [readOnly, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  const strokeColor = drawOverlayService[lessonElementId].currentStrokeColor;
  const fillColor = drawOverlayService[lessonElementId].currentFillColor;

  const overlayWidthPx = getDrawOverlayWidthPx(model);
  const widthPx = `${overlayWidthPx}px`;
  const heightPx = contentHeight > 0 ? `${contentHeight + DRAW_OVERLAY_TOOLBAR_HEIGHT_PX}px` : `${DRAW_OVERLAY_MIN_HEIGHT_PX + DRAW_OVERLAY_TOOLBAR_HEIGHT_PX}px`;

  const canvasId = `${model.id}_canvas`;
  const drawCanvasId = `${model.id}_drawCanvas`;

  let toolbar = MINIMUM_TOOLBAR;
  if (model.responseFormat === 'html') {
    if (model.toolbarType === 'mathToolbar') {
      toolbar = MATH_TOOLBAR;
    } else if (model.toolbarType === 'advancedToolbar') {
      toolbar = ADVANCED_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathAdvancedToolbar') {
      toolbar = ADVANCED_MATH_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathWithTableToolbar') {
      toolbar = MATH_WITH_TABLE_TOOLBAR;
    }
  }

  const shapeOptions = [
    { value: drawOverlayService[lessonElementId].TRIANGLE, label: <span className={`shapeIcon shapeIcon-${drawOverlayService[lessonElementId].TRIANGLE}`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].SQUARE, label: <span className={`shapeIcon shapeIcon-${drawOverlayService[lessonElementId].SQUARE}`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].CIRCLE, label: <span className={`shapeIcon shapeIcon-${drawOverlayService[lessonElementId].CIRCLE}`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].STAR, label: <span className={`shapeIcon shapeIcon-${drawOverlayService[lessonElementId].STAR}`}>&nbsp;</span> },
  ];

  const pointOptions = [
    { value: 'closed', label: <span className='pointIcon pointIcon-closed'>&nbsp;</span> },
    { value: 'open', label: <span className='pointIcon pointIcon-open'>&nbsp;</span> },
  ];

  const lineOptions = [
    { value: drawOverlayService[lessonElementId].LINE, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].LINE}`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].LINE}-dashed`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].LINE}-dashed`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].RAY, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].RAY}`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].RAY}-dashed`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].RAY}-dashed`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].SEGMENT, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].SEGMENT}`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].SEGMENT}-hidden-hidden`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].SEGMENT}-hidden-hidden`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].SEGMENT}-dashed`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].SEGMENT}-dashed`}>&nbsp;</span> },
    { value: drawOverlayService[lessonElementId].VECTOR, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].VECTOR}`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].VECTOR}-open-hidden`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].VECTOR}-open-hidden`}>&nbsp;</span> },
    { value: `${drawOverlayService[lessonElementId].VECTOR}-dashed`, label: <span className={`lineIcon lineIcon-${drawOverlayService[lessonElementId].VECTOR}-dashed`}>&nbsp;</span> },
  ];

  const handleChangeStrokeColor = (color) => {
    drawOverlayService[lessonElementId].setStrokeColor(color.hex);
  };

  const handleChangeFillColor = (color) => {
    drawOverlayService[lessonElementId].setFillColor(color.hex);
  };

  const handleChangeBrushSize = (data) => {
    ReactTooltip.hide();
    drawOverlayService[lessonElementId].setStrokeWidth(data.value);
  };

  const handleChangeShape = (data) => {
    ReactTooltip.hide();
    drawOverlayService[lessonElementId].addShape(data.value);
  };

  const handleChangeLine = (data) => {
    ReactTooltip.hide();
    drawOverlayService[lessonElementId].addLine(data.value);
  };

  const handleChangePoint = (data) => {
    ReactTooltip.hide();
    drawOverlayService[lessonElementId].addPoint(data.value);
  };

  const handleSelect = () => {
    drawOverlayService[lessonElementId].select();
  };

  const handleCopy = () => {
    drawOverlayService[lessonElementId].copy();
  };

  const handleUndo = () => {
    drawOverlayService[lessonElementId].undo();
  };

  const handleRedo = () => {
    drawOverlayService[lessonElementId].redo();
  };

  const handleDeleteText = (textId, leId) => {
    drawManager.deleteTextContainer(textId, leId);
    drawOverlayService[lessonElementId].updateState();
    drawOverlayService[lessonElementId].updateResponseModel();
  };

  const navToCurrentLessonElement = async () => {
    if (lessonManager.currentLessonElementId !== lessonElementId) {
      responseService.setSolutionDialogOpen(false);
      responseService.setHintDialogOpen(false);
      await navMenuService.navToSlideClickHandler(lessonElementId, { shouldScrollQuestionIntoView: false });
    }
  };

  const handleDragStart = (e) => {
    navToCurrentLessonElement();
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    drawManager.dragStartTextContainer(el.id, e.clientX, e.clientY, rect.width, rect.height, lessonElementId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    const overlayHeight = contentHeight > 0 ? contentHeight : DRAW_OVERLAY_MIN_HEIGHT_PX;
    drawManager.dropTextContainer(e.clientX, e.clientY, lessonElementId, overlayWidthPx, overlayHeight);
    drawOverlayService[lessonElementId].updateState();
    drawOverlayService[lessonElementId].updateResponseModel();
  };

  const textContainers = drawManager.textContainers[lessonElementId];

  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;

  if (responseDependencyValue) {
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId &&
                                responseDependencyValue.questionForeverId &&
                                responseDependencyValue.lessonId !== '' &&
                                responseDependencyValue.questionForeverId !== '';
      }
    } else {
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }
  const isSubmitted = lessonElementState ? lessonElementState.isSubmitted : false;

  const handleShowPreviousResponse = async () => {
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      if (isDifferentLessonSource) {
        if (PlayerService.isActivityMode()) {
          await lessonElementState.questionBehavior.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        } else {
          setShowPreviewUnavailable(true);
        }
        return;
      }

      if (lessonElementState.questionBehavior.hasCurrentContent(lessonElementId)) {
        setShowPreviousResponseConfirm(true);
      } else {
        lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
      }
    }
  };

  const handleConfirmShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
    }
  };

  const handleCancelShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
  };

  const handleConfirmSubmission = () => {
    setShowSubmissionWarning(false);
    responseService.submitResponse(lessonElementId);
  };

  const handleCancelSubmission = () => {
    setShowSubmissionWarning(false);
  };

  const hatMode = highlightAnnotationManager.highlightUserMode || 'off';
  const passThroughHighlights = (toolbarManager.isHighlightAnnotationOpen || toolbarManager.isToolOn('StickyNotes'))
    && hatMode !== 'off';

  return (
    <div className='draw-overlay-question  lesson-element stand-alone Draw'>
      {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && (
        <div className='testItemQuestion'>
          <PrintQuestionNumber model={model} />
          {!!directions && (
            <div className='directions'>
              <HtmlComponent htmlStr={directions} />
            </div>
          )}
          <HtmlComponent htmlStr={questionStemHtml} />
        </div>
      )}

      {hasResponseDependency && lessonManager.playerMode !== LessonMode.SCORING && lessonManager.playerMode !== LessonMode.REVIEW && (
        <div className='response-dependency-controls'>
          <ShowPreviousResponseButton
            lessonElementId={lessonElementId}
            onShowPreviousResponse={handleShowPreviousResponse}
            isDisabled={isSubmitted}
          />
        </div>
      )}

      <>
        {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && !!directions && (
          <div className='directions'>
            <HtmlComponent htmlStr={directions} />
          </div>
        )}
        {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && (





          <div className='draw-overlay-stage' id={`draw-overlay-stage-${lessonElementId}`} style={{ width: widthPx }}>
             <div className='drawToolbar' id={`drawToolbar-${lessonElementId}`} role='toolbar'>

                <ReactTooltip border effect='solid' id='draw' padding='10px'
                  type='light' />

                <div aria-label={t2('drawwithpencil')} className='pencilToolButton drawToolbarButton' data-for='draw' data-tip={t2('pencil\n')}
                  data-tool='pencil' onClick={() => !readOnly && drawOverlayService[lessonElementId].onClickPencilTool()} role='button'
                  tabIndex='0' style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                />

                <div aria-label={t2('drawwithpaintbrush')} className='paintToolButton drawToolbarButton' data-for='draw' data-tip={t2('brush')}
                  data-tool='paint' onClick={() => !readOnly && drawOverlayService[lessonElementId].onClickPaintTool()} role='button'
                  tabIndex='0' style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                />

                <div aria-label={t2('strokecolor')} className='strokeColorButton drawToolbarButton' data-for='draw' data-tip={t2('strokecolor')}
                  role='menu'
                  style={{ display: 'inline-block', opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  tabIndex={readOnly ? -1 : 0}>
                  <ColorPicker color={strokeColor} onChange={readOnly ? () => {} : handleChangeStrokeColor} disabled={readOnly} />
                </div>

                <div aria-label={t2('fillercolor')} className='fillColorButton drawToolbarButton' data-for='draw' data-tip={t2('fillercolor')}
                  role='menu'
                  style={{ display: 'inline-block', opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  tabIndex={readOnly ? -1 : 0}>
                  <ColorPicker color={fillColor} onChange={readOnly ? () => {} : handleChangeFillColor} disabled={readOnly} />
                </div>

                <div className='drawBrushSizeMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
                  <div aria-label={t2('brushsize')} className={`drawBrushSizeMenu drawToolMenu ${readOnly ? 'disabled' : ''}`} data-for='draw' data-tip={t2('brushsize')}
                    role='menu'
                    style={{ width: '40px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    tabIndex={readOnly ? -1 : 0}>
                    <Select
                      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                      onChange={readOnly ? () => {} : handleChangeBrushSize}
                      options={brushSizeOptions}
                      placeholder={brushSizePlaceholder}
                      styles={brushSizeCustomStyles}
                      isDisabled={readOnly}
                    />
                  </div>
                </div>

                <div className='shapeMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
                  <div aria-label={t2('shape')} className={`shapeMenu drawToolMenu ${readOnly ? 'disabled' : ''}`} data-for='draw' data-tip={t2('shape')}
                    role='menu'
                    style={{ width: '50px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    tabIndex={readOnly ? -1 : 0}>
                    <Select
                      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                      onChange={readOnly ? () => {} : handleChangeShape}
                      options={shapeOptions}
                      placeholder={shapePlaceholder}
                      styles={shapeCustomStyles}
                      isDisabled={readOnly}
                    />
                  </div>
                </div>

                {model.hasGraph && (
                <div className='lineMenuContainer' style={{ width: '60px', display: 'inline-block' }} tabIndex='-1'>
                  <div aria-label={t2('graphline')} className={`lineMenu drawToolMenu ${readOnly ? 'disabled' : ''}`} data-for='draw' data-tip={t2('graphline')}
                    role='menu' style={{ width: '60px' }} tabIndex='0'>
                    <Select
                      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                      onChange={readOnly ? () => {} : handleChangeLine}
                      options={lineOptions}
                      placeholder={linePlaceholder}
                      styles={lineCustomStyles}
                      isDisabled={readOnly}
                    />
                  </div>
                </div>
                )}

                {model.hasGraph && (
                <div className='pointMenuContainer' style={{ display: 'inline-block' }} tabIndex='-1'>
                  <div aria-label={t2('graphpoint')} className={`pointMenu drawToolMenu ${readOnly ? 'disabled' : ''}`} data-for='draw' data-tip={t2('graphpoint')}
                    role='menu' style={{ width: '50px' }} tabIndex='0'>
                    <Select
                      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, ValueContainer: CustomValueContainer }}
                      onChange={readOnly ? () => {} : handleChangePoint}
                      options={pointOptions}
                      placeholder={pointPlaceholder}
                      styles={pointCustomStyles}
                      isDisabled={readOnly}
                    />
                  </div>
                </div>
                )}

                <div aria-label={t2('selectitems')} className='selectToolButton drawToolbarButton' data-for='draw' data-tip={t2('selectitems')}
                  onClick={() => !readOnly && handleSelect()} role='button' tabIndex='0'
                  style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />

                <div aria-label={t2('addtext')} className='textToolButton drawToolbarButton' data-for='draw' data-tip={t2('addtext')}
                  onClick={() => !readOnly && drawOverlayService[lessonElementId].onClickTextTool()} role='button' tabIndex='0'
                  style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />

                <div className='rightButtonContainer' tabIndex='-1'>
                  <div aria-label={t2('copyelement')} className='drawCopyElementButton drawToolbarButton' data-for='draw' data-tip={t2('copyelement')}
                    onClick={() => !readOnly && handleCopy()} role='button' tabIndex='0'
                    style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                  <div aria-label={t2('undo')} className='drawUndoButton drawToolbarButton' data-for='draw' data-tip={t2('undo')}
                    onClick={() => !readOnly && handleUndo()} role='button' tabIndex='0'
                    style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                  <div aria-label={t2('redo')} className='drawRedoButton drawToolbarButton' data-for='draw' data-tip={t2('redo')}
                    onClick={() => !readOnly && handleRedo()} role='button' tabIndex='0'
                    style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                  <div aria-label={t2('deleteelement')} className='drawDeleteElementButton drawToolbarButton' data-for='draw' data-tip={t2('deleteelement')}
                    onClick={() => !readOnly && drawOverlayService[lessonElementId].deleteFromDraw()} role='button' tabIndex='0'
                    style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                </div>

                <div className='deleteButtonContainer'>
                  <button type='button' className='deleteElementButton' disabled={readOnly} style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}>{t('delete')}</button>
                  <button type='button' className='clearButton' disabled={readOnly} style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}>{t('clearAll')}</button>
                </div>
            </div>
            <div className='drawToolbarPlaceholder' id={`drawToolbarPlaceholder-${lessonElementId}`} aria-hidden='true' style={{ display: 'none' }} />
            <div className='draw-overlay-stage-inner' style={{  position: 'relative', width: '100%', height: heightPx }}>
            
             
            <div
              ref={textMeasureRef}
              className='draw-overlay-question-html testItemQuestion'
              id={`${lessonElementId}-draw-overlay-stem`}
              style={{ minHeight: `${DRAW_OVERLAY_MIN_HEIGHT_PX}px` }}
            >
              <HtmlComponent htmlStr={questionStemHtml} />
            </div>
            <div
              id={`${model.id}-draw-overlay-draw-wrapper`}
              className='draw-wrapper draw-overlay-draw-wrapper'
              style={{ position: 'absolute', left: 0, top: DRAW_OVERLAY_TOOLBAR_HEIGHT_PX, width: widthPx, height: 'calc(100% - DRAW_OVERLAY_TOOLBAR_HEIGHT_PX)' }}
            >
              <div className='canvasContainerOuter' id={`${model.id}_draw_overlay_canvas_outer`} onDragEnd={handleDragEnd}
                style={{ height: heightPx, width: widthPx }}>

                {textContainers.map((textContainer) => {
                  return (
                    <div key={textContainer.textId} className='drawTextContainer deleteButtonParent' draggable={!readOnly} id={textContainer.textId}
                      onDragStart={readOnly ? () => {} : handleDragStart} style={{ top: textContainer.top, left: textContainer.left, opacity: textContainer.opacity }}>
                      <div className='deleteButton deleteTextContainerButton'
                        onClick={() => !readOnly && handleDeleteText(textContainer.textId, lessonElementId)}
                        style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                      <div className='drawText oneRowToolbar cke_editable cke_editable_inline cke_contents_ltr cke_show_borders placeholder' id={`${textContainer.textId}_text`}>

                        <RichTextEditor
                          data={(textContainer.text) ? textContainer.text : ''}
                          lessonElementId={lessonElementId}
                          onBlur={() => {
                            if (!readOnly) {
                              drawOverlayService[lessonElementId].updateState();
                              drawOverlayService[lessonElementId].updateResponseModel();
                            }
                          }}
                          onChange={(data) => {
                            if (!readOnly) {
                              drawManager.setTextContainerText(textContainer.textId, data, lessonElementId);
                            }
                          }}
                          placeHolder={t('enterText')}
                          readOnly={readOnly}
                          toolbar={toolbar} />
                      </div>
                    </div>
                  );
                })}

                <div
                  className='draw-overlay-canvas-hit-layer'
                  style={{ pointerEvents: passThroughHighlights ? 'none' : 'auto' }}
                >
                  <div className='body canvasOrganizerBody canvasContainer draw-overlay-graph-canvas' id={`${model.id}_canvasContainer`} style={{ height: heightPx, width: widthPx }}>
                    <canvas className='canvas' id={canvasId} />
                  </div>

                  <div className='body canvasOrganizerBody drawCanvasContainer' id={`${model.id}_drawCanvasContainer`} style={{ width: widthPx, height: heightPx }}>
                    <canvas className='canvas' id={drawCanvasId} />
                  </div>
                </div>

                {readOnly && (
                  <div
                    className='draw-readonly-overlay'
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 10,
                      pointerEvents: 'all',
                      backgroundColor: 'transparent'
                    }}
                  />
                )}

              </div>
            </div>
            </div>

          </div>
        )}
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && (
          <div className='canvasContainerOuter' id='canvasContainerOuter' style={{ height: '1.5in', width: '1.5in' }} />
        )}
      </>
      {((lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && lessonManager.isForceTeacher) && (
        <div className='print-teacher-notes'>
          <HtmlComponent htmlStr={model.validation?.solution} />
        </div>
      )}

      <SubmissionWarningDialog
        isOpen={showSubmissionWarning}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelSubmission}
        message={t('submissionWarningMessage')}
      />

      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={handleConfirmShowPreviousResponse}
        onCancel={handleCancelShowPreviousResponse}
        message={t('showPreviousResponseWarning')}
      />

      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message="This feature is only available during live question answering sessions. It cannot be previewed in this mode."
      />

    </div>
  );
});

export default DrawOverlay;
